import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Lock, AlertCircle, Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import "../../LoginScreen.css";
import { passwordLoginSchema, type PasswordLoginInput } from "../../../../lib/validation/schemas/auth.schema";

interface PasswordStepProps {
  email: string;
  loading: boolean;
  serverError: string;
  onSubmit: (password: string) => void;
  onBack: () => void;
  onForgotPassword: () => void;
}

export default function PasswordStep({
  email,
  loading,
  serverError,
  onSubmit,
  onBack,
  onForgotPassword,
}: PasswordStepProps) {
  const { t } = useTranslation(["auth", "common"]);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<PasswordLoginInput>({
    resolver: zodResolver(passwordLoginSchema),
    defaultValues: { password: "" },
    mode: "onChange",
  });

  const errorMessage = errors.password?.message ?? serverError;

  return (
    <>
      <div className="ls-form-header">
        <button
          type="button"
          onClick={onBack}
          className="ls-header-back-button"
        >
          <ArrowLeft size={16} /> {t("common:actions.back")}
        </button>
        <h2 className="ls-welcome-title">{t("password.welcomeBackTitle")}</h2>
        <p className="ls-welcome-subtitle ls-welcome-subtitle--breakable">{email}</p>
      </div>

      {errorMessage && (
        <div className="ls-error-banner">
          <AlertCircle size={16} className="ls-error-icon" />
          <span>{errorMessage}</span>
        </div>
      )}

      <form
        onSubmit={handleSubmit((data) => onSubmit(data.password))}
        className="ls-form-fields"
      >
        <div className="ls-input-group">
          <label className="ls-input-label">{t("shared.passwordLabel")}</label>
          <div className="auth-input-container ls-input-container">
            <Lock size={18} className="ls-input-icon" />
            <input
              {...register("password")}
              type={showPassword ? "text" : "password"}
              placeholder={t("password.placeholder")}
              className="ls-form-input"
              disabled={loading}
              autoFocus
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="ls-eye-toggle"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          className={`ls-btn-primary${loading || !isValid ? " ls-btn-primary--disabled" : ""}`}
          disabled={loading || !isValid}
        >
          {loading ? (
            <Loader2 size={18} className="ls-spinner" />
          ) : (
            <Lock size={18} />
          )}
          <span>{loading ? t("password.signingIn") : t("password.signIn")}</span>
        </button>

        <button
          type="button"
          onClick={onForgotPassword}
          disabled={loading}
          className="ls-forgot-link"
        >
          {t("password.forgotPassword")}
        </button>
      </form>
    </>
  );
}
